/**
 * Ensec Currency Converter
 * Hård-kodad valutaomvandling (SEK → EUR / USD)
 * Fungerar på alla produktsidor, kollektionssidor, kundvagn osv.
 *
 * Rates: justera vid behov. Baseras på "1 SEK = X enheter".
 * Uppdaterad: 2026-08-18 — BTC-alternativet borttaget på ägarens begäran
 * (del av samma otillförlitliga krypto-integration som byggdes av en
 * tidigare, ej längre betrodd, administratör).
 * 2026-09-27 — Privat/Företag: visa priser exkl. 25 % moms (endast visning).
 */
(function () {
  'use strict';

  // KONFIG
  var STORAGE_KEY = 'ensecCurrency';
  var RATES_CACHE_KEY = 'ensecRatesCache';
  var RATES_TTL_MS = 60 * 60 * 1000; // 1 timme
  var BASE = 'SEK'; // Shopify-priser är alltid i SEK

  // Fallback-rates (används om live-API misslyckas)
  var RATES = {
    SEK: 1,
    EUR: 0.0898,   // 1 SEK ≈ 0.090 EUR
    USD: 0.0975    // 1 SEK ≈ 0.098 USD
  };

  var FORMATS = {
    SEK: { symbol: 'kr', position: 'after', decimals: 0, separator: ' ', decimalSep: ',' },
    EUR: { symbol: '€', position: 'before', decimals: 2, separator: ' ', decimalSep: '.' },
    USD: { symbol: '$', position: 'before', decimals: 2, separator: ',', decimalSep: '.' }
  };

  // LIVE RATES — hämtas från publika APIer, cachas 1h i localStorage
  function loadCachedRates() {
    try {
      var raw = localStorage.getItem(RATES_CACHE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !data.rates || !data.ts) return null;
      if (Date.now() - data.ts > RATES_TTL_MS) return null;
      return data.rates;
    } catch (e) { return null; }
  }

  function saveCachedRates(rates) {
    try {
      localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({
        rates: rates,
        ts: Date.now()
      }));
    } catch (e) {}
  }

  function fetchFiatRates() {
    return fetch('https://api.frankfurter.app/latest?from=SEK&to=EUR,USD')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.rates) return null;
        return {
          EUR: data.rates.EUR,
          USD: data.rates.USD
        };
      })
      .catch(function () { return null; });
  }

  function refreshLiveRates() {
    var cached = loadCachedRates();
    if (cached) {
      Object.assign(RATES, cached);
      return Promise.resolve(RATES);
    }
    return fetchFiatRates().then(function (fiat) {
      var updated = {};
      if (fiat && fiat.EUR) updated.EUR = fiat.EUR;
      if (fiat && fiat.USD) updated.USD = fiat.USD;
      if (Object.keys(updated).length > 0) {
        Object.assign(RATES, updated);
        saveCachedRates(updated);
      }
      return RATES;
    });
  }

  // STATE
  function getCurrency() {
    try {
      var saved = localStorage.getItem(STORAGE_KEY);
      if (saved && RATES.hasOwnProperty(saved)) return saved;
    } catch (e) {}
    return 'SEK';
  }

  var VAT_KEY = 'ensecVatMode';
  function isBusiness() {
    try { return localStorage.getItem(VAT_KEY) === 'foretag'; } catch (e) { return false; }
  }

  function setCurrency(code) {
    try { localStorage.setItem(STORAGE_KEY, code); } catch (e) {}
    document.documentElement.dataset.ensecCurrency = code;
  }

  // FORMATERING
  function formatAmount(sekValue, currency) {
    if (isNaN(sekValue)) return '';
    var rate = RATES[currency] || 1;
    var converted = sekValue * rate;
    var fmt = FORMATS[currency] || FORMATS.SEK;

    var dec = fmt.decimals;
    if (currency === 'SEK' && Math.round(converted * 100) % 100 !== 0) dec = 2;
    var fixed = converted.toFixed(dec);
    var parts = fixed.split('.');
    var intPart = parts[0];
    var decPart = parts[1] || '';

    // Tusen-separator (hårt mellanslag så att beloppet inte bryts på två rader)
    if (fmt.separator) {
      intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, fmt.separator === ' ' ? ' ' : fmt.separator);
    }

    var num = decPart ? intPart + fmt.decimalSep + decPart : intPart;

    if (fmt.position === 'before') {
      return fmt.symbol + ' ' + num;
    }
    return num + ' ' + fmt.symbol;
  }

  // PARSA SEK-VÄRDEN UR DOM
  var SEK_PATTERN = /(?:^|\s)((?:\d{1,3}(?:[\s,.]\d{3})*|\d+)(?:[.,]\d{1,2})?)\s*(?:kr|SEK|sek|:-)\b/;

  function extractSekValue(text) {
    if (!text) return null;
    var match = text.match(SEK_PATTERN);
    if (!match) return null;
    var raw = match[1];
    var hasComma = raw.indexOf(',') !== -1;
    var hasDot = raw.indexOf('.') !== -1;
    var hasSpace = raw.indexOf(' ') !== -1;

    if (hasComma && hasDot) {
      var lastComma = raw.lastIndexOf(',');
      var lastDot = raw.lastIndexOf('.');
      if (lastComma > lastDot) {
        raw = raw.replace(/\./g, '').replace(',', '.');
      } else {
        raw = raw.replace(/,/g, '');
      }
    } else if (hasComma) {
      raw = raw.replace(/\s/g, '').replace(',', '.');
    } else if (hasSpace) {
      raw = raw.replace(/\s/g, '');
    }
    var val = parseFloat(raw);
    return isNaN(val) ? null : val;
  }

  // KONVERTERA ALLA PRISELEMENT PÅ SIDAN
  var PRICE_SELECTORS = [
    '.price-item',           // Dawn standard
    '.money',                // Generisk Shopify
    '.price__regular .price-item',
    '.price__sale .price-item',
    '.ensec-butik__product-price',
    '.product-card-price',
    '[data-ensec-price]',
    '.ensec-product-price',
    '.ensec-deal',
    '.totals__total-value'
  ];

  function findPriceElements(root) {
    root = root || document;
    var seen = new Set();
    PRICE_SELECTORS.forEach(function (sel) {
      root.querySelectorAll(sel).forEach(function (el) { seen.add(el); });
    });
    return Array.from(seen);
  }

  function snapshotOriginal(el) {
    if (el.dataset.ensecSek !== undefined) return; // redan snapshotad
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var node, parts = [];
    while ((node = walker.nextNode())) {
      var sek = extractSekValue(node.textContent);
      if (sek !== null) {
        parts.push({ node: node, sek: sek, original: node.textContent });
      }
    }
    el._ensecPriceNodes = parts;
    el.dataset.ensecSek = parts.length > 0 ? '1' : '0';
  }

  function convertElement(el, currency) {
    snapshotOriginal(el);
    // Om temat har bytt ut pristexten (t.ex. produktsidans variant-rendering) – ta ny ögonblicksbild
    if (el._ensecPriceNodes && el._ensecPriceNodes.some(function (p) { return !el.contains(p.node); })) {
      delete el.dataset.ensecSek;
      snapshotOriginal(el);
    }
    if (!el._ensecPriceNodes || el._ensecPriceNodes.length === 0) return;

    var business = isBusiness();
    el._ensecPriceNodes.forEach(function (p) {
      if (currency === 'SEK' && !business) {
        if (p.node.textContent !== p.original) p.node.textContent = p.original;
        // ensec-translator.js återställer text till node._ensecOriginal – håll den i synk
        if (p.node._ensecOriginal !== undefined) p.node._ensecOriginal = p.original;
      } else {
        var formatted = formatAmount(business ? p.sek / 1.25 : p.sek, currency) + (business ? (document.documentElement.lang.indexOf('en') === 0 ? ' excl. VAT' : ' exkl. moms') : '');
        var newText = p.original.replace(SEK_PATTERN, function (match) {
          var leading = match.match(/^\s*/)[0];
          return leading + formatted;
        });
        if (p.node.textContent !== newText) p.node.textContent = newText;
        p.node._ensecOriginal = newText;
      }
    });
  }

  function applyCurrency(currency) {
    setCurrency(currency);
    findPriceElements().forEach(function (el) { convertElement(el, currency); });
    document.querySelectorAll('[data-ensec-curr-btn]').forEach(function (btn) {
      var btnCode = btn.dataset.ensecCurrBtn;
      btn.classList.toggle('ensec-curr__btn--active', btnCode === currency);
      btn.setAttribute('aria-pressed', btnCode === currency ? 'true' : 'false');
    });
  }

  // INIT
  function init() {
    var current = getCurrency();
    setCurrency(current);
    var cached = loadCachedRates();
    if (cached) Object.assign(RATES, cached);
    applyCurrency(current);
    document.querySelectorAll('[data-ensec-curr-btn]').forEach(function (btn) {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var code = btn.dataset.ensecCurrBtn;
        if (code && RATES.hasOwnProperty(code)) {
          applyCurrency(code);
        }
      });
    });
    refreshLiveRates().then(function () {
      applyCurrency(getCurrency());
    });
  }

  // OBSERVERA DOM-ÄNDRINGAR (Shopifys section reloads, AJAX-filter osv.)
  var rebindTimer;
  function rebind() {
    clearTimeout(rebindTimer);
    rebindTimer = setTimeout(function () {
      applyCurrency(getCurrency());
    }, 120);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (typeof MutationObserver !== 'undefined') {
    var obs = new MutationObserver(function (mutations) {
      var shouldRebind = false;
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (n) {
          if (n.nodeType === 1) shouldRebind = true;
        });
      });
      if (shouldRebind) rebind();
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  // Exponera API
  window.EnsecCurrency = {
    set: applyCurrency,
    get: getCurrency,
    business: isBusiness,
    rates: RATES,
    format: formatAmount,
    refresh: function () {
      try { localStorage.removeItem(RATES_CACHE_KEY); } catch (e) {}
      return refreshLiveRates().then(function () {
        applyCurrency(getCurrency());
        return RATES;
      });
    }
  };
})();
